import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Icon, Text } from '@reservamos/elements';
import snackIcon from 'images/icons/snacks-icon.png';
import snackPlaceholder from 'images/snack-placeholder.webp';

/**
 * SnacksShowcase component.
 * Render a showcase of snacks and beverages allowing quick selection.
 *
 * @param {object} props - catalog, onAddProduct, onViewAll.
 * @param {Array} props.catalog - List of products to showcase.
 * @param {function} props.onAddProduct - Callback when product is selected.
 * @param {function} props.onViewAll - Callback when "Ver todo" button is clicked.
 */
const SnacksShowcase = ({ catalog = [], onAddProduct, onViewAll }) => {
  const { t } = useTranslation('general');

  // Show only first 3 products from catalog
  const showcaseProducts = catalog.slice(0, 3);

  /**
   * Handle keyboard activation for role="button" elements.
   * @param {Function} callback - Function to call on Enter/Space.
   * @param {*} item - Item to pass to the callback.
   */
  const handleKeyDown = (callback, item) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (callback) {
        callback(item);
      }
    }
  };

  return (
    <Spacing vertical size="S">
      <Text size="L" mobileSize="M" weight="semibold">
        {t('snacks_summary.showcase_title')}
      </Text>
      <div className="flex justify-between md:justify-normal gap-5">
        {showcaseProducts.map((snack) => (
          <div
            key={snack.code}
            className="relative border border-gray border-solid rounded-md pointer cursor-pointer"
            onClick={() => onAddProduct && onAddProduct(snack)}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown(onAddProduct, snack)}
          >
            <img
              className="rounded-lg w-20 h-20"
              src={snack.image || snackPlaceholder}
              alt={snack.name}
              height="60"
              width="60"
            />
            <span className="bg-primary-regular rounded-full justify-center items-center flex h-7 w-7 absolute right-0 bottom-0">
              <Text elementType="span" color="white" weight="semibold">
                +
              </Text>
            </span>
          </div>
        ))}
        <div
          className="border border-gray border-solid rounded-md flex items-center justify-center gap-1 flex-col h-20 w-20 cursor-pointer"
          onClick={() => onViewAll && onViewAll()}
          onKeyDown={handleKeyDown(onViewAll)}
          role="button"
          tabIndex={0}
        >
          <Icon type={snackIcon} size="L" />
          <Text underline size="S" elementType="span">
            {t('snacks_summary.view_all')}
          </Text>
        </div>
      </div>
    </Spacing>
  );
};

SnacksShowcase.propTypes = {
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
    }),
  ),
  onAddProduct: PropTypes.func,
  onViewAll: PropTypes.func,
};

export default SnacksShowcase;
